import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import AwsS3 from '@uppy/aws-s3';
import { validateThumbnailDimensions } from './grouped_video_thumbnail_validation';

require('@uppy/core/dist/style.min.css');
require('@uppy/dashboard/dist/style.min.css');

const uploaders = new Map();

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

const requestJson = async (url, { method = 'GET', body } = {}) => {
  const headers = {
    accept: 'application/json',
    'Content-Type': 'application/json'
  };
  const token = csrfToken();
  if (token) headers['X-CSRF-Token'] = token;

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'thumbnail_request_failed');

  return payload;
};

const setError = (root, message = '') => {
  const error = root.querySelector('[data-thumbnail-error]');
  if (!error) return;

  error.textContent = message;
  error.classList.toggle('hidden', !message);
};

const setActionState = (root, uploaded) => {
  root.querySelector('[data-thumbnail-upload-trigger]')?.classList.toggle('hidden', uploaded);
  root.querySelector('[data-thumbnail-remove-trigger]')?.classList.toggle('hidden', !uploaded);
};

const setPreview = (root, url) => {
  const preview = root.querySelector('[data-thumbnail-preview]');
  const placeholder = root.querySelector('[data-thumbnail-placeholder]');
  if (!preview || !placeholder) return;

  preview.src = url;
  preview.classList.remove('hidden');
  placeholder.classList.add('hidden');
};

const clearPreview = (root) => {
  const preview = root.querySelector('[data-thumbnail-preview]');
  const placeholder = root.querySelector('[data-thumbnail-placeholder]');
  if (!preview || !placeholder) return;

  preview.removeAttribute('src');
  preview.classList.add('hidden');
  placeholder.classList.remove('hidden');
};

const closeDashboard = (uppy) => {
  setTimeout(() => uppy.getPlugin('Dashboard')?.closeModal(), 100);
};

/**
 * Initializes the grouping-owned Apple/YouTube thumbnail uploader.
 *
 * @param {HTMLElement} root thumbnail section containing endpoints and controls.
 * @returns {Uppy|null} initialized uploader.
 */
export const initializeGroupedVideoThumbnailUploader = (root) => {
  if (!root || root.dataset.thumbnailUploaderInitialized === 'true') return null;

  const uploadUrl = root.dataset.thumbnailUploadUrl;
  const previewUrl = root.dataset.thumbnailPreviewUrl;
  const deleteUrl = root.dataset.thumbnailDeleteUrl;
  const storageKey = document.getElementById('video_episode_thumbnail_storage_key');
  if (root.dataset.thumbnailReadOnly === 'true') {
    if (storageKey?.value && previewUrl) {
      requestJson(previewUrl)
        .then((payload) => setPreview(root, payload.download_url))
        .catch(() => setError(root, 'The saved thumbnail preview could not be loaded.'));
    }
    return null;
  }

  const uploadTrigger = root.querySelector('[data-thumbnail-upload-trigger]');
  const removeTrigger = root.querySelector('[data-thumbnail-remove-trigger]');
  const thumbnailChanged = root.closest('form')?.querySelector('[data-youtube-thumbnail-changed]');
  if (!uploadUrl || !previewUrl || !deleteUrl || !uploadTrigger || !removeTrigger || !storageKey) return null;

  let objectPreviewUrl = null;
  const uppy = new Uppy({
    id: `grouped-video-thumbnail-${uploadUrl}`,
    autoProceed: false,
    allowMultipleUploadBatches: false,
    restrictions: {
      maxFileSize: 2 * 1024 * 1024,
      minFileSize: 1024,
      maxTotalFileSize: 2 * 1024 * 1024,
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['image/jpeg', 'image/png']
    },
    locale: {
      strings: {
        youCanOnlyUploadFileTypes: 'Upload a JPG or PNG thumbnail.'
      }
    }
  });

  uppy.use(Dashboard, {
    trigger: uploadTrigger,
    closeAfterFinish: true,
    showProgressDetails: true,
    hideUploadButton: true,
    proudlyDisplayPoweredByUppy: false,
    note: 'Image must be exactly 1280px by 720px. Maximum 2MB file size.'
  });
  uppy.use(AwsS3, {
    async getUploadParameters(file) {
      const payload = await requestJson(uploadUrl, {
        method: 'POST',
        body: { content_type: file.type }
      });
      uppy.setFileMeta(file.id, { thumbnailStorageKey: payload.key });

      return {
        method: 'PUT',
        url: payload.upload_url,
        fields: {},
        headers: payload.headers
      };
    },
    shouldUseMultipart: false
  });

  uppy.on('file-added', async (file) => {
    setError(root);
    try {
      await validateThumbnailDimensions(file.data);
      await uppy.upload();
    } catch (error) {
      uppy.removeFile(file.id);
      setError(root, error?.message || 'Unable to validate this thumbnail. Please try another image.');
      closeDashboard(uppy);
    }
  });
  uppy.on('upload-error', (_file, error) => {
    setError(
      root,
      error?.isNetworkError
        ? 'Network error during upload. Check your connection and try again.'
        : 'Failed to upload the thumbnail. Please try again.'
    );
  });
  uppy.on('restriction-failed', (_file, error) => {
    setError(root, error?.message || 'Choose a JPG or PNG image under 2MB.');
  });
  uppy.on('complete', async (result) => {
    const file = result.successful?.[0];
    const newKey = file?.meta?.thumbnailStorageKey;
    if (!file || !newKey) return;

    const previousKey = storageKey.value;
    storageKey.value = newKey;
    if (thumbnailChanged) thumbnailChanged.value = 'true';
    if (objectPreviewUrl) URL.revokeObjectURL(objectPreviewUrl);
    objectPreviewUrl = URL.createObjectURL(file.data);
    setPreview(root, objectPreviewUrl);
    setActionState(root, true);
    setError(root);

    if (previousKey && previousKey !== newKey) {
      try {
        await requestJson(deleteUrl, {
          method: 'DELETE',
          body: { key: previousKey }
        });
      } catch (_error) {
        setError(root, 'The new thumbnail uploaded, but the previous image could not be removed.');
      }
    }
  });

  removeTrigger.addEventListener('click', async () => {
    if (!storageKey.value || removeTrigger.disabled) return;

    removeTrigger.disabled = true;
    setError(root);
    try {
      await requestJson(deleteUrl, {
        method: 'DELETE',
        body: { key: storageKey.value }
      });
      storageKey.value = '';
      if (thumbnailChanged) thumbnailChanged.value = 'false';
      if (objectPreviewUrl) URL.revokeObjectURL(objectPreviewUrl);
      objectPreviewUrl = null;
      clearPreview(root);
      setActionState(root, false);
      uppy.cancelAll();
    } catch (_error) {
      setError(root, 'Failed to remove the thumbnail. Please try again.');
    } finally {
      removeTrigger.disabled = false;
    }
  });

  if (storageKey.value) {
    setActionState(root, true);
    requestJson(previewUrl)
      .then((payload) => setPreview(root, payload.download_url))
      .catch(() => setError(root, 'The saved thumbnail preview could not be loaded.'));
  } else {
    setActionState(root, false);
  }

  root.dataset.thumbnailUploaderInitialized = 'true';
  root.groupedVideoThumbnailUppy = uppy;
  root.releaseGroupedVideoThumbnailPreview = () => {
    if (objectPreviewUrl) URL.revokeObjectURL(objectPreviewUrl);
  };
  uploaders.set(root, uppy);
  return uppy;
};

const initializeGroupedVideoThumbnailUploaders = () => {
  document.querySelectorAll('[data-grouped-video-thumbnail]')
    .forEach(initializeGroupedVideoThumbnailUploader);
};

export const closeGroupedVideoThumbnailUploaders = () => {
  uploaders.forEach((uppy, root) => {
    root.releaseGroupedVideoThumbnailPreview?.();
    uppy.close({ reason: 'unmount' });
    delete root.groupedVideoThumbnailUppy;
    delete root.releaseGroupedVideoThumbnailPreview;
    delete root.dataset.thumbnailUploaderInitialized;
  });
  uploaders.clear();
};

if (typeof document !== 'undefined') {
  document.addEventListener('turbo:load', initializeGroupedVideoThumbnailUploaders);
  document.addEventListener('turbo:before-cache', closeGroupedVideoThumbnailUploaders);
}
